import { forwardRef } from 'react';
import {
  ListItemAvatar as MuiListItemAvatar,
  ListItemAvatarProps as MuiListItemAvatarProps,
} from '@mui/material';

/**
 * ListItemAvatar component - Avatar container for list items.
 *
 * This is a thin wrapper around Material-UI's ListItemAvatar component that provides:
 * - Proper avatar sizing and spacing
 * - Left-aligned avatar positioning
 * - Vertical alignment with list item text
 * - Theme integration
 *
 * Note: This component should wrap an Avatar component. It handles the correct
 * spacing and alignment within the list item structure.
 *
 * Common use cases:
 * - User lists with profile pictures
 * - Contact lists
 * - Team member lists
 * - Message lists with sender avatars
 *
 * @example
 * // User list with avatar
 * <ListItem>
 *   <ListItemAvatar>
 *     <Avatar>JD</Avatar>
 *   </ListItemAvatar>
 *   <ListItemText primary="John Doe" secondary="john@example.com" />
 * </ListItem>
 *
 * @example
 * // With image avatar
 * <ListItem>
 *   <ListItemAvatar>
 *     <Avatar src="/user-photo.jpg" alt="User" />
 *   </ListItemAvatar>
 *   <ListItemText primary="User Name" />
 * </ListItem>
 */
export type ListItemAvatarProps = MuiListItemAvatarProps;

export const ListItemAvatar = forwardRef<HTMLDivElement, ListItemAvatarProps>((props, ref) => (
  <MuiListItemAvatar ref={ref} {...props} />
));

ListItemAvatar.displayName = 'ListItemAvatar';
